/**
 * Conversão dos arquivos de dados (TopoJSON/GeoJSON em lon/lat) para
 * geometrias OpenLayers na projeção do GOES.
 *
 * Dividida em duas etapas para rodar a parte pesada num Web Worker:
 * 1. `toFlatFeatures` (worker): TopoJSON → GeoJSON → coordenadas planas já
 *    projetadas (Float64Array transferível) + propriedades;
 * 2. `flatToOlFeatures` (thread principal): monta as features OL (rápido).
 */
import { feature as topoFeature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import MultiPoint from 'ol/geom/MultiPoint';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import type { TransformFunction } from 'ol/proj';
import type { ManifestLayer } from './types';

export type FlatGeometryType = 'Point' | 'MultiPoint' | 'LineString' | 'MultiLineString' | 'Polygon' | 'MultiPolygon';

/** Feature com coordenadas planas XY já na projeção do mapa. */
export interface FlatFeature {
  type: FlatGeometryType;
  coords: Float64Array;
  /** Fim de cada parte (MultiLineString, Polygon). */
  ends?: number[];
  /** Fins por polígono (MultiPolygon). */
  endss?: number[][];
  props: Record<string, unknown>;
}

type Position = number[];
interface GeoJsonGeometry {
  type: string;
  coordinates?: unknown;
}
interface GeoJsonFeature {
  type: 'Feature';
  geometry: GeoJsonGeometry | null;
  properties: Record<string, unknown> | null;
}

function pushLine(line: Position[], out: number[]): void {
  for (const c of line) out.push(c[0], c[1]);
}

/** Converte o JSON do arquivo em features planas projetadas (sem objetos OL). */
export function toFlatFeatures(
  json: unknown,
  meta: Pick<ManifestLayer, 'format' | 'object'>,
  transform: TransformFunction,
): FlatFeature[] {
  let data: unknown = json;
  if (meta.format === 'topojson') {
    const topology = json as Topology;
    if (!topology || topology.type !== 'Topology' || !topology.objects) throw new Error('TopoJSON inválido');
    const name = meta.object && topology.objects[meta.object] ? meta.object : Object.keys(topology.objects)[0];
    if (!name) throw new Error('TopoJSON sem objetos');
    data = topoFeature(topology, topology.objects[name]);
  }
  const d = data as { type?: string; features?: GeoJsonFeature[] } | null;
  if (!d || (d.type !== 'FeatureCollection' && d.type !== 'Feature')) throw new Error('GeoJSON inválido');
  const list: GeoJsonFeature[] = d.type === 'FeatureCollection' ? (d.features ?? []) : [d as unknown as GeoJsonFeature];

  const out: FlatFeature[] = [];
  for (const f of list) {
    const g = f?.geometry;
    if (!g || !g.coordinates) continue;
    const coords: number[] = [];
    let ends: number[] | undefined;
    let endss: number[][] | undefined;
    switch (g.type) {
      case 'Point': {
        const c = g.coordinates as Position;
        coords.push(c[0], c[1]);
        break;
      }
      case 'MultiPoint':
      case 'LineString':
        pushLine(g.coordinates as Position[], coords);
        break;
      case 'MultiLineString':
      case 'Polygon':
        ends = [];
        for (const part of g.coordinates as Position[][]) {
          pushLine(part, coords);
          ends.push(coords.length);
        }
        break;
      case 'MultiPolygon':
        endss = [];
        for (const poly of g.coordinates as Position[][][]) {
          const e: number[] = [];
          for (const ring of poly) {
            pushLine(ring, coords);
            e.push(coords.length);
          }
          endss.push(e);
        }
        break;
      default:
        continue; // GeometryCollection etc. não fazem parte do contrato
    }
    if (coords.length === 0) continue;
    transform(coords, coords, 2);
    // Fora do disco visível a projeção devolve Infinity: descarta a feature.
    if (!coords.every(Number.isFinite)) continue;
    out.push({ type: g.type as FlatGeometryType, coords: new Float64Array(coords), ends, endss, props: f.properties ?? {} });
  }
  return out;
}

function toGeometry(f: FlatFeature): Geometry {
  // Geometrias do OL esperam Array (não typed array) para operações como extend/push.
  const flat = Array.from(f.coords);
  switch (f.type) {
    case 'Point':
      return new Point(flat, 'XY');
    case 'MultiPoint':
      return new MultiPoint(flat, 'XY');
    case 'LineString':
      return new LineString(flat, 'XY');
    case 'MultiLineString':
      return new MultiLineString(flat, 'XY', f.ends ?? [flat.length]);
    case 'Polygon':
      return new Polygon(flat, 'XY', f.ends ?? [flat.length]);
    case 'MultiPolygon':
      return new MultiPolygon(flat, 'XY', f.endss ?? [[flat.length]]);
  }
}

/** Monta as features OpenLayers a partir das features planas. */
export function flatToOlFeatures(list: readonly FlatFeature[]): Feature<Geometry>[] {
  return list.map((f) => {
    const feature = new Feature<Geometry>(toGeometry(f));
    feature.setProperties(f.props, true);
    return feature;
  });
}
